import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  disableDetective, fixDetectiveRoles, getDetectiveAvailability, getDetectiveConfig,
  getDetectiveRolesJob, startDetectiveRolesScan, updateDetectiveRegions,
} from '../../../services/detectiveApi';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import MultiSelectField from './MultiSelectField';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import {
  CK, FONT_BODY, listCardSx, sectionCardSx, sectionHeadingSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

// Must match `color` in engines/detective.js.
const DETECTIVE_COLOR = '#3949AB';
const DETECTIVE_COLOR_DARK = '#283593';

const detectiveButtonSx = {
  backgroundColor: DETECTIVE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: DETECTIVE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const plainButtonSx = { fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' };

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const hintTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage regions, account permissions and Amazon Detective status"
    color={DETECTIVE_COLOR}
  />
);

// There is deliberately no scan-type tab. Amazon Detective has no
// UpdateResourceTypes analogue — its data sources (CloudTrail management events,
// VPC flow logs and GuardDuty findings) are selected and managed by AWS, so there
// is nothing here for an operator to turn on or off.

// DetectiveConfigResponse decodes linkingRegions to []string, but tolerate a raw
// JSON string so a DTO change cannot blank the form.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

const sameSet = (a, b) => a.length === b.length
  && [...a].sort().join('|') === [...b].sort().join('|');

// A region update claims the config atomically and is refused unless it is
// settled, so a Save dispatched during another operation is a guaranteed
// "another operation is in progress" error.
const configAllowsUpdate = (status) => status === 'enabled' || status === 'partial_failure';

// The region picker's options: AWS_REGIONS intersected with the allowlist the
// backend serves on GET /detective/availability, then unioned with whatever the
// config already stores.
//
// There is NO region list in this file. Detective is absent from two commercial
// regions and a JS copy of that fact would drift silently; the backend's guard is
// the real boundary and this only stops the operator reaching it.
//
// Both allowlist fallbacks degrade to MORE choice, never less: an absent array (an
// older backend, or an availability call that failed) and an intersection that
// comes out empty both fall back to the unfiltered list and let the backend refuse.
// An empty picker is indistinguishable from a broken page.
//
// The union with the STORED regions is what the setup wizard does not need. A
// region that is live in the config but missing from the allowlist — a region AWS
// has withdrawn, or an allowlist that shrank under a running deployment — would
// otherwise have no menu entry at all: it would still be selected, could not be
// deselected, and the one screen that could tear it down could not name it.
const buildRegionOptions = (supportedRegions, storedRegions) => {
  let base = AWS_REGIONS;
  if (Array.isArray(supportedRegions) && supportedRegions.length > 0) {
    const allow = new Set(supportedRegions);
    const filtered = AWS_REGIONS.filter((r) => allow.has(r.value));
    if (filtered.length > 0) base = filtered;
  }
  const known = new Set(base.map((r) => r.value));
  const extra = storedRegions
    .filter((r) => !known.has(r))
    .map((r) => ({ value: r, label: AWS_REGIONS.find((x) => x.value === r)?.label || r }));
  return [...base, ...extra];
};

export default function DetectiveSettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [regions, setRegions] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regionConfirmOpen, setRegionConfirmOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Extracted from the mount effect so the error state below can offer a real
  // retry. Seeding the region field from the response is safe here and only here:
  // this runs before there is a config, so there is no edit to lose.
  const loadConfig = useCallback(async () => {
    setLoading(true);
    // Its OWN try/catch, deliberately. A failed availability call must not decide
    // anything and must not abort the config load: a tenant whose Detective is
    // enabled and healthy has to keep working when this one auxiliary route is
    // unreachable. It stays null, which the picker reads as "no allowlist" and
    // degrades to the unfiltered region list. If the config load fails too, THAT is
    // what reports the error.
    try {
      const av = await getDetectiveAvailability();
      setAvailability(av?.data || null);
    } catch (_) {
      setAvailability(null);
    }
    try {
      const c = await getDetectiveConfig();
      const cfg = c?.data || null;
      setConfig(cfg);
      if (cfg) setRegions(toStringArray(cfg.linkingRegions));
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const operationRunning = config?.status === 'in_progress';

  // Every Save on this page is gated on a settled config, so without a poll a
  // visitor who arrives mid-operation would sit in front of permanently disabled
  // buttons until they reloaded by hand. Only the config is refreshed — never the
  // region field, which would otherwise overwrite an edit in progress.
  useEffect(() => {
    if (!operationRunning) return undefined;
    const id = setInterval(async () => {
      try {
        const c = await getDetectiveConfig();
        setConfig(c?.data || null);
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [operationRunning]);

  const save = async (fn, destination) => {
    setBusy(true);
    try {
      await fn();
      setErr(null);
      navigate(destination);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Hand off to Enrollment: a region change fans out over every live member
  // account — a new region needs a graph, an invitation and a verification per
  // account — and that page is where a per-account failure becomes actionable.
  const saveRegions = () => save(
    () => updateDetectiveRegions(regions),
    '/securityhub/detective/enrollment',
  );

  const closeDisableDialog = () => {
    setConfirmOpen(false);
    setConfirm('');
  };

  // The dialog closes BEFORE the call. Left until after the await, a rejection
  // would sit behind the still-open modal on top of the page-level error banner:
  // the button re-enables, the user clicks again, and nothing appears to happen.
  //
  // Destination is the Detective entry — it polls the config and narrates the
  // teardown, then shows the setup wizard once the status reaches "disabled".
  const doDisable = () => {
    closeDisableDialog();
    return save(() => disableDetective(), '/securityhub/detective');
  };

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Detective settings…" color={DETECTIVE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  // MUST precede the not-configured branch. GET /config rejecting leaves `config`
  // null, and the branch below would then tell an operator whose Detective is
  // configured and running that it was never set up — while discarding the actual
  // error, which is already sitting in `err`.
  if (err && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={err} onRetry={loadConfig} />
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/detective')}>
              Go to setup
            </Button>
          }>
            Amazon Detective is not set up yet for this customer. Complete the setup
            wizard first to manage regions, account permissions, or disable the
            service.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const storedRegions = toStringArray(config.linkingRegions);
  const regionOptions = buildRegionOptions(availability?.supportedRegions, storedRegions);
  const canUpdate = configAllowsUpdate(config.status);
  // "failed" is terminal, not transitional: no fan-out is running, and the update
  // gate refuses it until setup is re-run. Without this the Save button is dead
  // with no explanation whatsoever. Disable stays available — the backend accepts
  // it from every settled status — which is the other way out of this state.
  const setupFailed = config.status === 'failed';
  // The home region is force-prepended server-side and locked in the picker, so it
  // can never appear here; anything that does is genuinely being torn down.
  const removedRegions = storedRegions.filter((r) => !regions.includes(r));
  const addedRegions = regions.filter((r) => !storedRegions.includes(r));
  const regionsDirty = !sameSet(regions, storedRegions);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
      {err && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'A Detective operation is running — settings are locked until it finishes'}
            sx={{ mb: 2 }}
          />
        </motion.div>
      )}

      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/detective')}>
                Re-run setup
              </Button>
            )}
          >
            Amazon Detective setup failed{config.errorMessage ? `: ${config.errorMessage}` : '.'}
            {' '}
            Nothing is running and nothing will resume on its own. Regions cannot be
            changed until setup is re-run — disabling Detective from the Danger zone
            still works, and is what releases the member slots a partial enable is
            still holding.
          </Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={listCardSx}>
        <Tabs
          value={tab}
          onChange={(_, v) => { setErr(null); setTab(v); }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${CK.border}`,
            '& .MuiTab-root': {
              fontFamily: FONT_BODY,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              minHeight: 56,
              color: CK.body,
              '&.Mui-selected': { color: DETECTIVE_COLOR },
            },
            '& .MuiTabs-indicator': { backgroundColor: DETECTIVE_COLOR },
          }}
        >
          <Tab label="Regions" />
          <Tab label="Permissions" />
          <Tab label="Danger zone" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Regions</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Home region: <b>{config.homeRegion}</b> (read-only). The behavior graph
              belongs to the administrator account in each region, and the
              administrator cannot be changed without disabling Detective.
            </Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              This list is not a display filter. Amazon Detective builds one behavior
              graph <b>per region</b>, so adding a region creates a new graph there,
              invites every enrolled account into it — each invitation is verified
              against the account&rsquo;s AWS root user email address again — and
              starts billing. Removing a region deletes that region&rsquo;s graph and
              the investigations Prism has cached from it.
            </Typography>
            <Typography sx={{ ...hintTextSx, mb: 2 }}>
              Amazon Detective is not available in every AWS region. The list below is
              filtered to the regions this deployment supports; if that list could not
              be read, every region is offered and the backend refuses the ones it
              cannot use.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <MultiSelectField
                label="Regions"
                options={regionOptions}
                value={regions}
                onChange={setRegions}
                lockedValues={config.homeRegion ? [config.homeRegion] : []}
              />
            </Box>
            {removedRegions.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Saving will delete the behavior {removedRegions.length === 1 ? 'graph' : 'graphs'} in
                {' '}<b>{removedRegions.join(', ')}</b> — along with everything
                {removedRegions.length === 1 ? ' it has' : ' they have'} accumulated.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={busy || !canUpdate || !regionsDirty}
              onClick={() => (removedRegions.length > 0 ? setRegionConfirmOpen(true) : saveRegions())}
              sx={detectiveButtonSx}
            >
              Save regions
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <RolePermissionsPanel
              capabilityLabel="Amazon Detective"
              startScan={startDetectiveRolesScan}
              getJob={getDetectiveRolesJob}
              fix={fixDetectiveRoles}
            />
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Disable Amazon Detective</Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              Disabling <b>deletes the behavior graph in every region</b>. A behavior
              graph is not a view over data held somewhere else — it is the analysis
              Detective has built up from your CloudTrail, VPC flow log and GuardDuty
              activity, and it is deleted with the graph. <b>That history is not
              recoverable.</b> There is no export and no restore: re-enabling Detective
              later creates a new, empty graph that starts accumulating from that
              moment, and the historical window an investigation could have reached
              back into is gone.
            </Alert>
            <Typography sx={{ ...bodyTextSx, mb: 2 }}>
              Every member account is removed from the graph — which releases the
              member slots they hold, including the ones held by accounts that never
              finished joining — and the investigations Prism has cached are deleted.
              On a clean teardown Prism also detaches the IAM policy it added to each
              enrolled account&rsquo;s access role.
            </Typography>
            <Button
              color="error"
              variant="contained"
              disabled={busy || operationRunning}
              onClick={() => setConfirmOpen(true)}
              sx={plainButtonSx}
            >
              Disable Amazon Detective
            </Button>
          </Box>
        )}
      </Paper>
      </motion.div>

      <Dialog
        open={regionConfirmOpen}
        onClose={() => setRegionConfirmOpen(false)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Confirm region removal</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Removing <b>{removedRegions.join(', ')}</b> deletes the behavior
            {removedRegions.length === 1 ? ' graph' : ' graphs'} there. The analysis
            {removedRegions.length === 1 ? ' it has' : ' they have'} accumulated is
            deleted with {removedRegions.length === 1 ? 'it' : 'them'} and is not
            recoverable, every enrolled account is removed from
            {removedRegions.length === 1 ? ' that graph' : ' those graphs'}, and the
            investigations Prism cached from
            {removedRegions.length === 1 ? ' that region' : ' those regions'} are
            deleted here. Adding the {removedRegions.length === 1 ? 'region' : 'regions'}
            {' '}back later builds a new graph from scratch.
          </Typography>
          {addedRegions.length > 0 && (
            <Typography sx={{ ...bodyTextSx, mt: 2 }}>
              The same save creates a graph in <b>{addedRegions.join(', ')}</b> and
              invites every enrolled account into it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRegionConfirmOpen(false)}
            sx={{ ...plainButtonSx, color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => { setRegionConfirmOpen(false); saveRegions(); }}
            sx={plainButtonSx}
          >
            Save regions
          </Button>
        </DialogActions>
      </Dialog>

      {/* The typed customer ID is cleared on every exit. Left in state, reopening
          the dialog arrives with the type-to-confirm guard already satisfied and
          Disable live on the first click — which is the whole thing the guard
          exists to prevent. */}
      <Dialog
        open={confirmOpen}
        onClose={closeDisableDialog}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Confirm disable</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            The behavior graph in every region, and all of the history it has
            accumulated, will be deleted and cannot be recovered. Type the customer ID
            {' '}<b>{config.customerId}</b> to confirm:
          </Typography>
          <TextField
            fullWidth
            label="Customer ID"
            placeholder={config.customerId}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeDisableDialog}
            sx={{ ...plainButtonSx, color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || confirm !== config.customerId}
            onClick={doDisable}
            sx={plainButtonSx}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
